import {Component, OnInit} from '@angular/core';
import {Device, DeviceInfo} from '@capacitor/device';
import {takeUntil} from 'rxjs/operators';
import {Destroyable} from '../components/destroyable';
import {NGXLogger} from 'ngx-logger';
import {Store} from '@ngrx/store';
import {isAuthenticated} from 'gamesis-state';
import {AuthService} from 'gamesis-auth';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {NotificationProvider} from '../services/notification';
import {NavController} from '@ionic/angular';

enum Mode {
    Login = 0,
    Register = 1,
    Recover = 2
}

@Component({
    template: `
        <ion-content [fullscreen]="true">
            <ion-grid class="ion-no-padding">
                <ion-row>
                    <ion-col class="ion-hide-md-down"></ion-col>
                    <ion-col logo
                             [logoSize]="500"
                             class="ion-text-center w-100">
                        <form>
                            @switch (mode) {
                                @case (modes.Login) {
                                    Welcome to ga.me.sis, please sign in

                                    @if (info?.platform !== 'android') {
                                        <ion-button fill='clear' expand='full'
                                                    color="primary"
                                                    (click)="authService.openAppleSignIn()">
                                            <ion-icon name="logo-apple" slot="start"></ion-icon>
                                            Sign in with Apple
                                        </ion-button>
                                    }

                                    <ion-button fill='clear' expand='full'
                                                color="primary"
                                                (click)="authService.openGoogleSignIn()">
                                        <ion-icon name="logo-google" slot="start"></ion-icon>
                                        Sign in with Google
                                    </ion-button>
                                    <hr>
                                    <ion-list lines="full" inset>
                                        <ion-item color="light">
                                            <app-input type="email"
                                                       label="Email"
                                                       errorText="Please enter a valid email address"
                                                       [control]="email">
                                            </app-input>
                                        </ion-item>
                                        <ion-item color="light">
                                            <app-input type="password"
                                                       [control]="password"
                                                       label="Password"
                                                       errorText="Password is invalid">
                                            </app-input>
                                        </ion-item>
                                        <ion-button [color]="loginForm.invalid ? 'dark': 'primary'"
                                                    class="ion-no-margin"
                                                    [disabled]="loginForm.invalid"
                                                    (click)="login()"
                                                    expand="full">
                                            Sign in
                                        </ion-button>
                                    </ion-list>
                                    <ion-button fill="clear"
                                                expand="full"
                                                color="black"
                                                class="ion-no-margin"
                                                (click)="mode = modes.Recover">
                                        Forgot password?
                                    </ion-button>
                                    <ion-button fill="clear"
                                                expand="full"
                                                color="black"
                                                class="ion-no-margin"
                                                (click)="mode = modes.Register">
                                        Create account
                                    </ion-button>
                                }
                                @case (modes.Register) {
                                    <ion-list lines="full" [inset]="true">
                                        <ion-item color="light">
                                            <app-input type="email"
                                                       label="Email"
                                                       errorText="Please enter a valid email address"
                                                       [control]="registerEmail">
                                            </app-input>
                                        </ion-item>
                                        <ion-item color="light">
                                            <app-input type="password"
                                                       [control]="registerPassword"
                                                       label="Password"
                                                       errorText="Password is invalid">
                                            </app-input>
                                        </ion-item>
                                        <ion-item color="light">
                                            <app-input type="password"
                                                       [control]="repeat"
                                                       label="Repeat Password"
                                                       errorText="Passwords must match">
                                            </app-input>
                                        </ion-item>
                                        <ion-button [color]="registerForm.invalid ? 'dark': 'primary'"
                                                    class="ion-no-margin"
                                                    [disabled]="registerForm.invalid"
                                                    (click)="register()"
                                                    expand="full">
                                            Create account
                                        </ion-button>
                                    </ion-list>
                                    <app-label class="monospaced">
                                        <small class="ion-text-left">
                                            <p>Passwords must meet the following requirements:</p>
                                            <ul>
                                                <li>
                                                    <ion-text
                                                            [color]="registerPassword.dirty && (registerPassword.hasError('minlength') || registerPassword.hasError('maxlength')) ? 'danger': null">
                                                        Be between 8 and 4096 characters long
                                                    </ion-text>
                                                </li>
                                                <li>
                                                    <ion-text
                                                            [color]="registerPassword.dirty && registerPassword.hasError('containsUppercase') ? 'danger': null">
                                                        Include at least one uppercase letter
                                                    </ion-text>
                                                </li>
                                                <li>
                                                    <ion-text
                                                            [color]="registerPassword.dirty && registerPassword.hasError('containsLowercase') ? 'danger': null">
                                                        Include at least one lowercase letter
                                                    </ion-text>
                                                </li>
                                                <li>
                                                    <ion-text
                                                            [color]="registerPassword.dirty && registerPassword.hasError('containsNumber') ? 'danger': null">
                                                        Include at least one number
                                                    </ion-text>
                                                </li>
                                            </ul>
                                        </small>
                                    </app-label>
                                }
                                @case (modes.Recover) {
                                    <app-label>Please enter your email.<br>We will send you a link to reset your password.</app-label>
                                    <ion-list lines="full" [inset]="true">
                                        <ion-item color="light">
                                            <app-input type="email"
                                                       label="Email"
                                                       errorText="Please enter a valid email address"
                                                       [control]="email">
                                            </app-input>
                                        </ion-item>
                                        <ion-button [color]="email.invalid ? 'dark': 'primary'"
                                                    class="ion-no-margin"
                                                    [disabled]="email.invalid"
                                                    (click)="recover()"
                                                    expand="full">
                                            Recover
                                        </ion-button>
                                    </ion-list>
                                }
                            }

                            @if (mode != modes.Login) {
                                <ion-button fill="clear"
                                            expand="full"
                                            color="black"
                                            class="ion-no-margin"
                                            (click)="mode = modes.Login">
                                    Back to login
                                </ion-button>
                            }
                        </form>

                    </ion-col>
                    <ion-col class="ion-hide-md-down"></ion-col>
                </ion-row>
            </ion-grid>
        </ion-content>
    `,
    standalone: false
})
export class LoginPage extends Destroyable implements OnInit {
    public info: DeviceInfo;
    public modes = Mode;
    public mode = Mode.Login;
    public loginForm: FormGroup;
    public registerForm: FormGroup;
    public email: FormControl;
    public registerEmail: FormControl;
    public password: FormControl;
    public registerPassword: FormControl;
    public repeat: FormControl;

    constructor(public authService: AuthService,
                public store: Store,
                private navCtrl: NavController,
                private logger: NGXLogger,
                private formBuilder: FormBuilder,
                private notifications: NotificationProvider) {
        super();
    }

    ngOnInit() {
        Device.getInfo().then(info => {
            this.info = info;
        });

        this.email = this.formBuilder.control(null, [
            Validators.required,
            Validators.email]
        );
        this.registerEmail = this.formBuilder.control(null, [
            Validators.required,
            Validators.email]
        );
        this.password = this.formBuilder.control(null, [
            Validators.required,
            Validators.minLength(8)]
        );
        this.registerPassword = this.formBuilder.control(null, [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(4096),
            this.customPatternValidator({
                containsUppercase: /[A-Z]/,
                containsLowercase: /[a-z]/,
                containsNumber: /\d/
            })]
        );
        this.repeat = this.formBuilder.control(null, [
            Validators.required, this.confirmPasswordValidator(this.registerPassword)]
        );

        this.loginForm = this.formBuilder.group({
            email: this.email,
            password: this.password
        });
        this.registerForm = this.formBuilder.group({
            email: this.registerEmail,
            password: this.registerPassword,
            repeat: this.repeat
        });

        this.store.select(isAuthenticated)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(authenticated => {
                if (authenticated) {
                    this.logger.debug('authenticated, redirecting to campaign selection');
                    this.navCtrl.navigateRoot(['/select']);
                }
            });
    }

    login(): void {
        this.authService.firebaseSignin(this.loginForm.value).then(() => {
            this.loginForm.reset();
        }).catch(() => {
            // login failed, reset the form
            this.notifications.error('Login failed.');
            this.password.reset();
        });
    }

    register(): void {
        this.authService.register(this.registerForm.value)
            .then(() => {
                this.notifications.success('Registration was successful. Please check your email.');
                this.mode = Mode.Login;
            })
            .catch((message) => {
                this.notifications.error(message);
            });
    }

    recover(): void {
        this.authService.recover(this.email.value)
            .then(() => {
                this.notifications.success('Please check your email.');
                this.mode = Mode.Login;
            })
            .catch((message) => {
                this.notifications.error(message);
            });
    }

    confirmPasswordValidator = (passwordControl: AbstractControl): ValidatorFn => {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.parent) {
                return null; // Skip validation if parent does not exist yet
            }

            if (!passwordControl) {
                return null; // Skip validation if password control does not exist
            }

            const passwordsMatch = passwordControl.value === control.value;
            return passwordsMatch ? null : {passwordMismatch: true};
        };
    }


    customPatternValidator = (patterns: { [key: string]: RegExp }): ValidatorFn => {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null; // Don't validate if the field is empty
            }

            const errors: ValidationErrors = {};

            for (const [errorKey, pattern] of Object.entries(patterns)) {
                if (!pattern.test(control.value)) {
                    errors[errorKey] = true; // Add a custom error key
                }
            }

            return Object.keys(errors).length ? errors : null;
        };
    }
}
